import { motion } from "framer-motion";

export default function Section({ children, className = "", id, ...rest }) {
  return (
    <section id={id} className={`py-24 md:py-32 ${className}`} {...rest}>
      {children}
    </section>
  );
}

export function Container({ children, className = "" }) {
  return <div className={`max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 ${className}`}>{children}</div>;
}

export function Overline({ children, className = "" }) {
  return (
    <div className={`text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-5 ${className}`}>
      {children}
    </div>
  );
}

export function SectionHeading({ overline, title, kicker, children, align = "left" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}
    >
      {overline && <Overline>{overline}</Overline>}
      <h2 className="text-4xl sm:text-5xl md:text-6xl font-serif font-black leading-[1.05] text-balance">
        {title}
      </h2>
      {kicker && <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl text-pretty">{kicker}</p>}
      {children}
    </motion.div>
  );
}

export function PageHero({ overline, title, kicker, bg, image }) {
  // If neither bg nor image is provided, default to a tasteful congregation backdrop
  const effectiveImage = image || (!bg ? "/images/branding/congregation-family.jpg" : null);
  return (
    <div className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
      {effectiveImage ? (
        <>
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${effectiveImage})` }}
          />
          {/* Dark gradient overlay so headline text stays legible */}
          <div className="absolute inset-0 z-0 bg-gradient-to-r from-wine-950/95 via-wine-950/75 to-wine-950/40" />
          <div className="absolute inset-0 z-0 bg-gradient-to-b from-wine-950/60 via-transparent to-wine-950/90" />
        </>
      ) : (
        <div
          className="absolute inset-0 z-0"
          style={{
            background: bg ||
              "radial-gradient(ellipse at 30% 20%, rgba(122,26,46,0.55), transparent 60%), linear-gradient(180deg, #170B10 0%, #0A0507 100%)",
          }}
        />
      )}
      <div className="absolute inset-0 bg-grain pointer-events-none" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {overline && <Overline>{overline}</Overline>}
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif font-black leading-[0.95] tracking-tight text-balance max-w-4xl">
          {title}
        </h1>
        {kicker && <p className="mt-8 text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl text-pretty">{kicker}</p>}
      </div>
    </div>
  );
}
